import '../../styles/PaymentsSettings.css';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { PlanSelectionModal } from '../../components/subscription/PlanSelectionModal';
import {
  createStripePortalSession,
  fetchSubscriptionSnapshot,
  findPlanBoat,
  getSubscriptionSnapshot,
} from '../../services/subscription';

export const PaymentsSettings = () => {
  const { t } = useTranslation('common');
  const [snapshot, setSnapshot] = useState(() => getSubscriptionSnapshot());
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const selectedBoat = snapshot.subscription ? findPlanBoat(snapshot.subscription.boatId) : null;

  useEffect(() => {
    let isMounted = true;
    const loadSubscription = async (): Promise<void> => {
      try {
        const nextSnapshot = await fetchSubscriptionSnapshot();
        if (isMounted) setSnapshot(nextSnapshot);
      } catch {
        // Keep the local snapshot when backend status cannot be reached.
      }
    };

    void loadSubscription();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleManageBilling = async (): Promise<void> => {
    setIsPortalLoading(true);
    setBillingError('');
    try {
      const url = await createStripePortalSession('/settings/payments');
      window.location.href = url;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('payments.portalError', 'Stripe portal is not available.');
      setBillingError(message);
    } finally {
      setIsPortalLoading(false);
    }
  };

  return (
    <div className="settings-form-container-payments">
      <h2 className="settings-form-title-payments">{t('nav.paymentsSubscriptions')}</h2>

      <div className="payment-message-payments payment-plan-panel">
        <h3>{t('payments.planTitle', 'Plan y suscripción')}</h3>
        <p>
          {t(
            'payments.planDesc',
            'Modifica tu plan y gestiona la facturación desde Stripe.',
          )}
        </p>

        <div className="payment-plan-summary">
          <div>
            <span>{t('payments.status', 'Estado')}</span>
            <strong>
              {snapshot.status === 'trialing'
                ? t('payments.trialActive', 'Trial activo')
                : snapshot.status === 'unlimited'
                  ? t('payments.unlimitedPlan', 'Acceso ilimitado')
                  : snapshot.status === 'active'
                    ? t('payments.activePlan', 'Plan activo')
                    : snapshot.status === 'expired'
                      ? t('payments.expiredPlan', 'Plan expirado')
                      : t('payments.noActivePlan', 'Sin plan activo')}
            </strong>
          </div>
          <div>
            <span>{t('payments.selectedBoat', 'Barco')}</span>
            <strong>{selectedBoat?.name ?? t('payments.pendingSelection', 'Pendiente')}</strong>
          </div>
          <div>
            <span>{t('payments.billing', 'Facturación')}</span>
            <strong>
              {snapshot.subscription?.billingPeriod === 'year'
                ? t('payments.yearly', 'Anual')
                : snapshot.subscription?.billingPeriod === 'month'
                  ? t('payments.monthly', 'Mensual')
                  : snapshot.subscription?.billingPeriod === 'day'
                    ? t('payments.daily', 'Diario')
                    : '-'}
            </strong>
          </div>
        </div>

        <div className="payment-plan-actions">
          <button
            type="button"
            className="payment-plan-button"
            onClick={() => setIsPlanModalOpen(true)}
          >
            {t('payments.changePlan', 'Cambiar plan')}
          </button>
          {snapshot.stripeCustomerId && !snapshot.isUnlimited && (
            <button
              type="button"
              className="payment-plan-button secondary"
              onClick={handleManageBilling}
              disabled={isPortalLoading}
            >
              {isPortalLoading
                ? t('payments.openingStripe', 'Abriendo Stripe...')
                : t('payments.manageBilling', 'Gestionar en Stripe')}
            </button>
          )}
        </div>
        {billingError && <p className="payment-plan-error">{billingError}</p>}
      </div>
      <PlanSelectionModal
        open={isPlanModalOpen}
        reason="change-plan"
        mode={snapshot.stripeSubscriptionId && snapshot.isUsable ? 'change' : 'checkout'}
        allowClose
        onClose={() => setIsPlanModalOpen(false)}
        onPlanChanged={(nextSnapshot) => setSnapshot(nextSnapshot)}
      />
    </div>
  );
};
